
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { TreatmentServiceCard } from "@/components/TreatmentServiceCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageTitle } from "@/components/PageTitle";

const ToothWhitening = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQs specific to Tooth Whitening
  const faqs = [
    {
      question: "How does professional teeth whitening work?",
      answer: "Professional teeth whitening at Asrani Dental in Sadar, Nagpur uses high-concentration peroxide gels that break down stains on tooth enamel. In our in-office procedure, we apply protective barriers to your gums, then place the whitening gel on your teeth and activate it with a special light for enhanced results. For take-home whitening, we provide custom-fitted trays and professional-grade whitening gel for you to use at home."
    },
    {
      question: "Is professional teeth whitening safe?",
      answer: "Yes, professional teeth whitening performed at Asrani Dental Clinic in Nagpur is very safe when administered by our trained dental professionals. We take precautions to protect your gums and soft tissues, and we evaluate your dental health beforehand to ensure you're a good candidate. Professional whitening is much safer than over-the-counter options since it's customized to your specific needs."
    },
    {
      question: "How long do teeth whitening results last?",
      answer: "Results from our professional whitening treatments in Nagpur typically last from 6 months to 2 years, depending on your habits and maintenance. Factors that affect longevity include consumption of staining foods and beverages (coffee, tea, red wine, etc.), tobacco use, and oral hygiene practices. We provide maintenance recommendations to help extend your results."
    },
    {
      question: "Will teeth whitening work on all types of stains?",
      answer: "Professional teeth whitening at our Nagpur clinic works best on extrinsic stains caused by foods, drinks, and smoking. It may not be as effective on intrinsic stains (those inside the tooth) caused by medications, trauma, or fluorosis. During your consultation at Asrani Dental, Sadar, we'll assess your specific case and recommend the most appropriate whitening approach or alternative cosmetic solutions if needed."
    },
    {
      question: "Is teeth whitening painful or does it cause sensitivity?",
      answer: "Some patients may experience temporary sensitivity during or after teeth whitening. At Asrani Dental in Nagpur, we take measures to minimize sensitivity, including using desensitizing agents before and after treatment. Any sensitivity typically resolves within a few days. For patients with naturally sensitive teeth, we can adjust treatment intensity or recommend specialized products to make the experience more comfortable."
    }
  ];

  // Testimonials for Tooth Whitening
  const testimonials = [
    {
      name: "Rohit Kumar",
      comment: "After years of drinking coffee, my teeth had become noticeably stained. The in-office whitening treatment at Asrani Dental in Sadar, Nagpur was quick, comfortable, and the results were immediate! My teeth are now several shades whiter, and I've received so many compliments on my smile.",
      rating: 5
    },
    {
      name: "Neha Singh",
      comment: "I chose the take-home whitening kit from Asrani Dental Clinic in Nagpur because it fit better with my schedule. Dr. Asrani created custom trays that fit perfectly, and the results after two weeks were amazing. The team gave clear instructions and checked in to ensure everything was going well. I'm thrilled with how natural yet bright my smile looks.",
      rating: 5
    },
    {
      name: "Ajay Verma",
      comment: "I was worried about sensitivity since I've had issues with over-the-counter whitening products before. The professional treatment at Asrani Dental in Nagpur was completely different - virtually no sensitivity and incredible results! The staff was thorough in explaining the process and made sure I was comfortable throughout.",
      rating: 5
    }
  ];

  const handleBookAppointment = () => {
    window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank");
  };

  return (
    <div className="min-h-screen">
      <PageTitle title="Tooth Whitening in Nagpur | Cost of Tooth Whitening in Nagpur" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Tooth Whitening in Nagpur
            </h1>
            <p className="text-lg text-dental-muted">
              Achieve a brilliantly white, confident smile with our professional teeth whitening services at Asrani Dental, Sadar, Nagpur.
            </p>
            <div className="mt-8">
              <Button className="btn-primary" onClick={handleBookAppointment}>
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/lovable-uploads/toothwhitening.jpg" 
                alt="Tooth Whitening at Asrani Dental Clinic, Sadar, Nagpur" 
                className="rounded-xl shadow-soft w-full h-auto"
              />
            </div>
            
            <div>
              <SectionTitle 
                title="Professional Teeth Whitening" 
                subtitle="Tooth Whitening"
                description="At Asrani Dental in Sadar, Nagpur, our professional teeth whitening services provide dramatic, long-lasting results that brighten your smile and boost your confidence."
                alignment="left"
              />
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Dramatic Results</h3>
                    <p className="text-dental-muted">Professional whitening can brighten teeth by several shades in just one appointment.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Safe and Effective</h3>
                    <p className="text-dental-muted">Treatment is supervised by dental professionals to ensure safety and maximum effectiveness.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Customized Solutions</h3>
                    <p className="text-dental-muted">Whitening treatments are tailored to your specific needs, goals, and sensitivity levels.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Long-lasting Results</h3>
                    <p className="text-dental-muted">Professional treatments provide longer-lasting whiteness compared to over-the-counter products.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Treatments Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle 
            title="Our Teeth Whitening Services" 
            subtitle="Services We Offer"
            description="At Asrani Dental in Sadar, Nagpur, we offer multiple professional whitening options to fit your needs and lifestyle."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <TreatmentServiceCard 
              title="In-Office Power Whitening"
              description="Single-visit professional whitening that delivers dramatic results in just 60-90 minutes, perfect for quick smile enhancement."
              backgroundColor="bg-white"
            />
            
            <TreatmentServiceCard 
              title="Custom Take-Home Whitening"
              description="Professional-grade whitening using custom-fitted trays that you can use at home for gradual, controlled whitening on your schedule."
              backgroundColor="bg-[#F5F2EE]"
            />
            
            <TreatmentServiceCard 
              title="Combination Whitening"
              description="Start with in-office whitening for immediate results, followed by take-home maintenance for the most comprehensive whitening experience."
              backgroundColor="bg-[#E5DEFF]"
            />
            
            <TreatmentServiceCard 
              title="Whitening for Sensitive Teeth"
              description="Specialized whitening formulations and techniques for patients with dental sensitivity, ensuring comfort without compromising results."
              backgroundColor="bg-[#F7F5FF]"
            />
            
            <TreatmentServiceCard 
              title="Stain Prevention Guidance"
              description="Professional advice and products to help maintain your whitened smile and prevent new stains from forming."
              backgroundColor="bg-[#EEFBF3]"
            />
            
            <TreatmentServiceCard 
              title="Whitening Touch-Up Treatments"
              description="Quick maintenance treatments to refresh your whitening results periodically and keep your smile bright."
              backgroundColor="bg-[#FFF8F0]"
            />
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle 
            title="Patient Testimonials" 
            subtitle="What Our Patients Say"
            description="Hear from our patients about their teeth whitening experiences at Asrani Dental in Sadar, Nagpur."
            alignment="center"
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <ServiceTestimonials testimonials={testimonials} />
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common Questions"
            description="Find answers to common questions about teeth whitening at Asrani Dental, Nagpur."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>
      
      {/* Next Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">Ready for a Brighter Smile?</h2>
            <p className="text-lg text-dental-muted mb-8">
              Contact Asrani Dental in Sadar, Nagpur today to schedule your teeth whitening consultation. Our expert team is ready to help you achieve a brilliantly white smile.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button className="btn-primary flex items-center text-base group" onClick={handleBookAppointment}>
                Book Appointment
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              
              <Button className="btn-secondary flex items-center text-base" onClick={() => window.location.href = '/contact'}>
                Contact Us
              </Button>
            </div>
            
            <div className="mt-10">
              <p className="text-dental-muted mb-4">Interested in our other dental services in Nagpur?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/cosmetic-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Cosmetic Dentistry
                </Link>
                <Link to="/services/smile-makeover" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Smile Makeover
                </Link>
                <Link to="/services/general-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  General Dentistry
                </Link>
                <Link to="/services/teeth-cleaning" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Teeth Cleaning
                </Link>
                <Link to="/services/laser-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Laser Dentistry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CTASection
        title="Achieve a Dazzling White Smile"
        description="At Asrani Dental in Sadar, Nagpur, our professional teeth whitening services can help you achieve a brilliant, confident smile. Schedule your consultation today."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Learn More"
        primaryButtonAction={handleBookAppointment}
        secondaryButtonAction={() => window.location.href = "/about"}
      />
      
      <Footer />
    </div>
  );
};

export default ToothWhitening;
